import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  Button,
  Skeleton,
  SimpleGrid,
  Alert,
  Card,
  Table,
  ActionIcon,
  Menu,
  Select,
} from "@mantine/core";
import {
  IconPackage,
  IconAlertCircle,
  IconHistory,
  IconPlus,
  IconCalendarEvent,
  IconTrash,
  IconAdjustmentsHorizontal,
  IconDotsVertical,
} from "@tabler/icons-react";
import moment from "moment";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useSalonScopedFetch } from "../../hooks/useSalonScopedFetch";
import usePermissions from "../../hooks/usePermissions";
import { BusinessInfo, SalonContextType } from "../../types/context";
import {
  ClientServicePackage,
  ClientPackageAdjustment,
} from "../../types/packages";
import AddPackageToClientModal from "./AddPackageToClientModal";
import AdjustBankModal from "./AdjustBankModal";
import EditExpiryModal from "./EditExpiryModal";
import RevokePackageModal from "./RevokePackageModal";
import PackageHistoryModal from "./PackageHistoryModal";
import styles from "./ClientPackagesSection.module.css";

interface ClientPackagesSectionProps {
  clientId: string;
  /**
   * @deprecated Retained for call-site compatibility. This section is now a
   * read-only view of a client's owned packages — packages are sold via Quick
   * Sale, not from the client profile — so this flag no longer changes render.
   */
  readOnly?: boolean;
  sectionTitle?: string;
  sectionSubtitle?: string;
}

const statusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "active":
      return "var(--brand-lime-green)";
    case "revoked":
      return "red";
    case "depleted":
    case "expired":
      return "gray";
    default:
      return "gray";
  }
};

/** Client-side status filter options; "all" shows every owned package. */
const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "depleted", label: "Depleted" },
  { value: "expired", label: "Expired" },
  { value: "refunded", label: "Refunded" },
  { value: "revoked", label: "Revoked" },
];
const DEFAULT_STATUS_FILTER = "active";

/** Revoke/edit-expiry are blocked server-side once a package is terminal. */
const isTerminalStatus = (status: string): boolean =>
  ["revoked", "refunded"].includes(status.toLowerCase());

/** Bank adjustments are blocked on terminal packages AND once expired. */
const isAdjustableStatus = (status: string): boolean =>
  !isTerminalStatus(status) && status.toLowerCase() !== "expired";

/** Total remaining units across every service bank (all forfeited on revoke). */
const sumRemaining = (pkg: ClientServicePackage): number =>
  pkg.services.reduce((total, s) => total + s.quantity_remaining, 0);

interface PackageCardProps {
  pkg: ClientServicePackage;
  salonId: string;
  getAdjustments: (
    salonId: string,
    cspId: string,
  ) => Promise<ClientPackageAdjustment[]>;
  /** True when the viewer may manage packages (permission-gated affordances). */
  canManage: boolean;
  onEditExpiry: (pkg: ClientServicePackage) => void;
  onRevoke: (pkg: ClientServicePackage) => void;
  /** Opens the Adjust Bank modal for this package (which service is picked there). */
  onAdjust: (pkg: ClientServicePackage) => void;
  /** Bumped after any successful action to reload an already-loaded history. */
  historyToken: number;
}

/** One owned package: balances plus an actions menu (history opens in a modal). */
function PackageCard({
  pkg,
  salonId,
  getAdjustments,
  canManage,
  onEditExpiry,
  onRevoke,
  onAdjust,
  historyToken,
}: PackageCardProps) {
  const [historyOpen, setHistoryOpen] = useState(false);

  const showPackageActions = canManage && !isTerminalStatus(pkg.status);
  const showBankActions = canManage && isAdjustableStatus(pkg.status);

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Group gap="xs" align="center" wrap="nowrap">
              <Text fw={600}>{pkg.name}</Text>
              <Badge size="sm" variant="light" color={statusColor(pkg.status)}>
                {pkg.status}
              </Badge>
            </Group>
            {pkg.expires_at && (
              <Text size="xs" c="dimmed">
                Expires {moment(pkg.expires_at).format("MMM D, YYYY")}
              </Text>
            )}
          </Stack>
          <Menu position="bottom-end" shadow="md" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Actions for ${pkg.name}`}
              >
                <IconDotsVertical size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconHistory size={16} />}
                onClick={() => setHistoryOpen(true)}
              >
                View history
              </Menu.Item>
              {showBankActions && (
                <Menu.Item
                  leftSection={<IconAdjustmentsHorizontal size={16} />}
                  onClick={() => onAdjust(pkg)}
                >
                  Adjust bank
                </Menu.Item>
              )}
              {showPackageActions && (
                <Menu.Item
                  leftSection={<IconCalendarEvent size={16} />}
                  onClick={() => onEditExpiry(pkg)}
                >
                  Edit expiry
                </Menu.Item>
              )}
              {showPackageActions && (
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={() => onRevoke(pkg)}
                >
                  Revoke package
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Table verticalSpacing="xs" withRowBorders={false}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Service</Table.Th>
              <Table.Th ta="right">Remaining</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {pkg.services.map((service) => (
              <Table.Tr key={service.salon_service_id}>
                <Table.Td>{service.service_name}</Table.Td>
                <Table.Td ta="right">
                  <Text
                    fw={500}
                    c={
                      service.quantity_remaining > 0
                        ? "var(--brand-teal)"
                        : "dimmed"
                    }
                  >
                    {service.quantity_remaining} / {service.quantity_total}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>

      <PackageHistoryModal
        opened={historyOpen}
        onClose={() => setHistoryOpen(false)}
        salonId={salonId}
        cspId={pkg.id}
        packageName={pkg.name}
        getAdjustments={getAdjustments}
        historyToken={historyToken}
      />
    </Card>
  );
}

export default function ClientPackagesSection({
  clientId,
  sectionTitle,
  sectionSubtitle,
}: ClientPackagesSectionProps) {
  const { getClientServicePackages, getClientPackageAdjustments } = useApp();
  const { businessInfo } = useSalon() as SalonContextType;
  const salonId = (businessInfo as BusinessInfo | null)?.id ?? "";
  const perms = usePermissions();
  // Per-action gate is purely the manage permission (the feature itself is
  // always available now that Packages & Specials is enabled).
  const canManage = perms.can("packages", "canManageClientPackages");

  const [packages, setPackages] = useState<ClientServicePackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>(
    DEFAULT_STATUS_FILTER,
  );

  // Action-modal targets (null = closed). Add has no target beyond the client.
  const [addOpen, setAddOpen] = useState(false);
  const [expiryTarget, setExpiryTarget] = useState<ClientServicePackage | null>(
    null,
  );
  const [revokeTarget, setRevokeTarget] = useState<ClientServicePackage | null>(
    null,
  );
  const [adjustTarget, setAdjustTarget] = useState<ClientServicePackage | null>(
    null,
  );
  // Bumped after any successful action to reload already-loaded histories.
  const [historyToken, setHistoryToken] = useState(0);

  // Filter by status (default Active) then sort soonest-upcoming-expiry first;
  // packages that never expire (null) sort to the bottom.
  const visiblePackages = useMemo(() => {
    const filtered =
      statusFilter === "all"
        ? packages
        : packages.filter((p) => p.status.toLowerCase() === statusFilter);
    return [...filtered].sort((a, b) => {
      if (!a.expires_at) return 1;
      if (!b.expires_at) return -1;
      return Date.parse(a.expires_at) - Date.parse(b.expires_at);
    });
  }, [packages, statusFilter]);

  const fetchPackages = useCallback(async () => {
    if (!clientId || !salonId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Client-profile management shows inactive (depleted/expired/revoked)
      // packages too, not just active ones.
      const list = await getClientServicePackages(salonId, clientId, true);
      setPackages(list);
    } catch (err) {
      console.error("Error fetching client packages:", err);
      setError("Failed to load packages.");
    } finally {
      setIsLoading(false);
    }
  }, [clientId, salonId, getClientServicePackages]);

  // Without ids there is nothing to fetch — clear the initial spinner (the
  // scoped fetch below only fires when both ids exist).
  useEffect(() => {
    if (!clientId || !salonId) setIsLoading(false);
  }, [clientId, salonId]);

  // Fetch ONCE per (salon, client) — multi-location loop fix. The old effect
  // depended on fetchPackages' identity (→ getClientServicePackages →
  // fetchWithAuth), so upstream identity churn refired it unbounded when the
  // backend 403s. Failures stop at the error state; a salon/client change
  // fires exactly one fresh fetch, and the error Alert's Retry re-arms it.
  const { retry: retryPackages } = useSalonScopedFetch(
    salonId && clientId ? `${salonId}:${clientId}` : null,
    fetchPackages,
    { tag: "client-service-packages" },
  );

  // After any successful action: refetch the owned-packages list and signal
  // loaded histories to reload so the audit trail stays current.
  const handleActionSuccess = useCallback(() => {
    retryPackages();
    setHistoryToken((token) => token + 1);
  }, [retryPackages]);

  const renderHeadingRow = () => (
    <Group
      justify="space-between"
      align="flex-start"
      wrap="wrap"
      gap="md"
      className={sectionTitle ? styles.sectionHead : undefined}
    >
      {sectionTitle ? (
        <Stack gap={4}>
          <Text fw={600} size="lg" c="dark.8">
            {sectionTitle}
          </Text>
          {sectionSubtitle ? (
            <Text size="sm" c="dimmed">
              {sectionSubtitle}
            </Text>
          ) : null}
        </Stack>
      ) : (
        <Group gap="xs">
          <IconPackage size={18} />
          <Text fw={500}>Service Packages</Text>
          {packages.length > 0 && (
            <Badge size="sm" variant="light" color="gray">
              {packages.length}
            </Badge>
          )}
        </Group>
      )}
      {canManage && (
        <Button
          color="var(--brand-teal)"
          leftSection={<IconPlus size={16} />}
          onClick={() => setAddOpen(true)}
        >
          Add package
        </Button>
      )}
    </Group>
  );

  if (isLoading) {
    return (
      <Stack gap="xs">
        {renderHeadingRow()}
        <Skeleton height={80} radius="md" />
        <Skeleton height={80} radius="md" />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {renderHeadingRow()}

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setError(null)}
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button
              size="compact-xs"
              variant="light"
              color="red"
              onClick={retryPackages}
            >
              Retry
            </Button>
          </Group>
        </Alert>
      )}

      {packages.length === 0 ? (
        <Paper p="md" withBorder className={styles.emptyState}>
          <Stack align="center" gap="xs">
            <IconPackage size={32} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed" ta="center">
              No packages owned
            </Text>
          </Stack>
        </Paper>
      ) : (
        <>
          <Group justify="flex-end">
            <Select
              aria-label="Filter packages by status"
              data={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(value) =>
                setStatusFilter(value ?? DEFAULT_STATUS_FILTER)
              }
              size="xs"
              w={150}
              allowDeselect={false}
            />
          </Group>

          {visiblePackages.length === 0 ? (
            <Paper p="md" withBorder className={styles.emptyState}>
              <Stack align="center" gap="xs">
                <IconPackage size={32} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="dimmed" ta="center">
                  No {statusFilter} packages
                </Text>
              </Stack>
            </Paper>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {visiblePackages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  salonId={salonId}
                  getAdjustments={getClientPackageAdjustments}
                  canManage={canManage}
                  onEditExpiry={setExpiryTarget}
                  onRevoke={setRevokeTarget}
                  onAdjust={setAdjustTarget}
                  historyToken={historyToken}
                />
              ))}
            </SimpleGrid>
          )}
        </>
      )}

      {canManage && (
        <AddPackageToClientModal
          opened={addOpen}
          onClose={() => setAddOpen(false)}
          salonId={salonId}
          clientId={clientId}
          onSuccess={handleActionSuccess}
        />
      )}

      {expiryTarget && (
        <EditExpiryModal
          opened
          onClose={() => setExpiryTarget(null)}
          salonId={salonId}
          cspId={expiryTarget.id}
          currentExpiry={expiryTarget.expires_at}
          onSuccess={handleActionSuccess}
        />
      )}

      {revokeTarget && (
        <RevokePackageModal
          opened
          onClose={() => setRevokeTarget(null)}
          salonId={salonId}
          cspId={revokeTarget.id}
          packageName={revokeTarget.name}
          remainingUnits={sumRemaining(revokeTarget)}
          onSuccess={handleActionSuccess}
        />
      )}

      {adjustTarget && (
        <AdjustBankModal
          opened
          onClose={() => setAdjustTarget(null)}
          salonId={salonId}
          cspId={adjustTarget.id}
          services={adjustTarget.services}
          onSuccess={handleActionSuccess}
        />
      )}
    </Stack>
  );
}
